import { XtreamClient } from './xtream.js';
import { MediaServerClient } from './mediaserver.js';
import { PlexClient } from './plex.js';
import { FilesClient } from './files.js';
import { ArchiveClient } from './archive.js';
import { storageBridge } from './storage-bridge.js';

/* Tipos de proveedor y el cliente que habla con cada uno. Las listas M3U no tienen cliente:
   la propia lista trae las direcciones. */

export const PROVIDER_TYPES = [
    { id: 'xtream', group: 'iptv', icon: 'live_tv', fields: 'account' },
    { id: 'm3u', group: 'iptv', icon: 'playlist_play', fields: 'm3u' },
    { id: 'jellyfin', group: 'library', icon: 'video_library', fields: 'account' },
    { id: 'emby', group: 'library', icon: 'video_library', fields: 'account' },
    { id: 'plex', group: 'library', icon: 'video_library', fields: 'token' },
    { id: 'files', group: 'files', icon: 'folder_open', fields: 'folder' },
    // Almacenamiento que lee la propia app (puente nativo): un recurso SMB de la red de casa y una carpeta de este aparato.
    { id: 'smb', group: 'storage', icon: 'dns', fields: 'smb' },
    { id: 'local', group: 'storage', icon: 'smartphone', fields: 'local' },
    // Nubes: se entra en la página del servicio (cloud-auth.js); el permiso es solo de lectura y lo guarda el aparato.
    { id: 'dropbox', group: 'storage', icon: 'cloud', fields: 'cloud' },
    { id: 'onedrive', group: 'storage', icon: 'cloud', fields: 'cloud' },
    // Cine de dominio público de Internet Archive: sin campos, se añade desde «Canales gratuitos».
    { id: 'archive', group: 'archive', icon: 'theaters', fields: 'none' },
    // No es un tipo que se guarde: al elegir una lista pública se crea un proveedor M3U con ella.
    { id: 'free', group: 'free', icon: 'public', fields: 'preset' }
];

export function providerType(id) {
    return PROVIDER_TYPES.find((x) => x.id === id) || PROVIDER_TYPES[0];
}

/**
 * Lo que pone main.js cuando hay servidor de Filoteca:
 *  - mediaServerAuth(body): inicio de sesión en Jellyfin/Emby a través del servidor.
 *  - filesFetch({ url, method, headers }) → { status, text }: leer una carpeta a través del servidor.
 *  - lookup(kind, item): ficha de TMDB de un título (para las carátulas de los archivos propios).
 */
export const hooks = { mediaServerAuth: null, filesFetch: null, lookup: null };

const DIRECT_TIMEOUT = 8000;

/**
 * Petición a un servidor de ficheros. Primero directa; si el navegador no deja (CORS, contenido
 * mixto), a través del servidor. Recuerda cuál de las dos funcionó para no repetir el intento.
 */
function filesRequest() {
    let via = '';
    const direct = async (url, opts) => {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = setTimeout(() => { if (controller) controller.abort(); }, DIRECT_TIMEOUT);
        try {
            const r = await fetch(url, { method: opts.method, headers: opts.headers, cache: 'no-store', signal: controller ? controller.signal : undefined });
            return { status: r.status, text: await r.text() };
        } finally {
            clearTimeout(timer);
        }
    };
    return async (url, opts) => {
        const mixed = typeof location !== 'undefined' && location.protocol === 'https:' && /^http:/i.test(url);
        if (via !== 'server' && !mixed) {
            try {
                const r = await direct(url, opts);
                via = 'direct';
                return r;
            } catch (e) {
                if (via === 'direct' || !hooks.filesFetch) throw e;
            }
        }
        if (!hooks.filesFetch) throw Object.assign(new Error('unreachable'), { code: 'unreachable' });
        const r = await hooks.filesFetch({ url, method: opts.method, headers: opts.headers });
        via = 'server';
        return r;
    };
}

/**
 * Consulta de TMDB para las carátulas de los archivos propios y de «Cine clásico». Se decide en cada uso (no al crear el cliente):
 * la clave puede ponerse en Ajustes después de arrancar la app. Sin ella falla sin guardar nada, y la próxima vez se vuelve a intentar.
 */
const lazyLookup = () => async (kind, item) => {
    if (!hooks.lookup) throw Object.assign(new Error('no-lookup'), { code: 'no-lookup' });
    return hooks.lookup(kind, item);
};

/** Lo que sirve el puente está en este mismo aparato: se pide siempre directo (el servidor de Filoteca no llegaría). */
async function bridgeRequest(url, opts) {
    const r = await fetch(url, { method: opts.method, headers: opts.headers, cache: 'no-store' });
    return { status: r.status, text: await r.text() };
}

export function makeClient(profile) {
    if (!profile) return null;
    if (profile.type === 'xtream') return new XtreamClient(profile);
    if (profile.type === 'jellyfin' || profile.type === 'emby') return new MediaServerClient(profile, { authFallback: hooks.mediaServerAuth });
    if (profile.type === 'plex') return new PlexClient(profile);
    // «Vídeos personales» (casilla del Hub o del aparato): sin consultar TMDB, para que no se les ponga la carátula de otra película.
    if (profile.type === 'files') return new FilesClient(profile, { request: filesRequest(), lookup: profile.personal && profile.personal !== '0' ? null : lazyLookup() });
    if (profile.type === 'smb' || profile.type === 'local' || profile.type === 'dropbox' || profile.type === 'onedrive') return new FilesClient(profile, { request: bridgeRequest, lookup: profile.personal && profile.personal !== '0' ? null : lazyLookup(), bridge: storageBridge });
    if (profile.type === 'archive') return new ArchiveClient(profile, { lookup: lazyLookup() });
    return null;
}
